import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import GoogleMap from '@/components/ui/GoogleMap';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { LocationDetailsForm, ConnectionInfoForm } from '@/components/forms';
import ReportService from '@/services/reportService';
import type { Coordinates, MarkerFormData, ReportFormData, Marker, Report } from '@/types';

interface LocationState {
  marker?: Marker;
  report?: Report;
}

const EditReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { markerId, reportId } = useParams<{ markerId: string; reportId: string }>();
  const location = useLocation();
  const state = location.state as LocationState;

  const [selectedPosition, setSelectedPosition] = useState<Coordinates | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [marker, setMarker] = useState<Marker | null>(null);
  const [report, setReport] = useState<Report | null>(null);

  // Form state
  const [formData, setFormData] = useState<MarkerFormData>({
    coordinates: { latitude: 0, longitude: 0 },
    streetName: '',
    streetNameCorrection: '',
    currentLine: {
      operator: '',
      technology: 'Unknown',
      maxSpeed: {
        download: 0,
        upload: 0,
        unit: 'Mbps'
      },
      isActivated: false
    }
  });

  const [reportData, setReportData] = useState<ReportFormData>({
    issue: 'no_fiber',
    description: '',
    priority: 'medium',
    reportedBy: {
      name: '',
      username: '',
      email: '',
      phone: ''
    }
  });

  useEffect(() => {
    if (state?.marker && state?.report) {
      // Data passed from MyReports
      initializeFromState(state.marker, state.report);
    } else if (markerId && reportId) {
      // Need to fetch data
      fetchMarkerAndReport();
    } else {
      // Invalid state
      toast.error('Dati mancanti per la modifica');
      navigate('/my-reports');
    }
  }, [markerId, reportId, state]);

  const initializeFromState = (markerData: Marker, reportData: Report) => {
    setMarker(markerData);
    setReport(reportData);

    setSelectedPosition(markerData.coordinates);
    setFormData({
      coordinates: markerData.coordinates,
      streetName: markerData.streetName,
      streetNameCorrection: markerData.streetNameCorrection || '',
      currentLine: markerData.currentLine
    });

    setReportData({
      issue: reportData.issue,
      description: reportData.description || '',
      priority: reportData.priority,
      reportedBy: reportData.reportedBy || { name: '', username: '', email: '', phone: '' }
    });

    setIsInitialLoading(false);
  };

  const fetchMarkerAndReport = async () => {
    if (!markerId || !reportId) return;

    try {
      setIsInitialLoading(true);
      const data = await ReportService.getUserReports();
      
      const foundMarker = data.markers.find(m => m._id === markerId);
      const foundReport = foundMarker?.reports.find(r => r._id === reportId);

      if (!foundMarker || !foundReport) {
        toast.error('Segnalazione non trovata');
        navigate('/my-reports');
        return;
      }

      initializeFromState(foundMarker, foundReport);
    } catch (error: any) {
      console.error('Error fetching marker and report:', error);
      toast.error('Errore nel caricamento dei dati');
      navigate('/my-reports');
    }
  };

  const handleMapClick = useCallback((coordinates: Coordinates) => {
    setSelectedPosition(coordinates);
    setFormData(prev => ({
      ...prev,
      coordinates
    }));
  }, []);

  const handleAddressSelect = useCallback((address: string, coordinates: Coordinates) => {
    setSelectedPosition(coordinates);
    setFormData(prev => ({
      ...prev,
      coordinates,
      streetName: address
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPosition) {
      toast.error('Seleziona una posizione sulla mappa');
      return;
    }

    if (!formData.streetName.trim()) {
      toast.error('Inserisci il nome della via');
      return;
    }

    if (!marker || !report) {
      toast.error('Dati mancanti');
      return;
    }

    setIsLoading(true);

    try {
      const updateData = {
        markerData: {
          coordinates: selectedPosition,
          streetName: formData.streetName,
          streetNameCorrection: formData.streetNameCorrection,
          currentLine: formData.currentLine
        },
        reportData: {
          reportId: report._id,
          issue: reportData.issue,
          description: reportData.description,
          priority: reportData.priority
        }
      };

      await ReportService.updateUserMarker(marker._id, updateData);
      
      toast.success('Segnalazione aggiornata con successo!');
      navigate('/my-reports');
    } catch (error: any) {
      console.error('Update error:', error);
      const message = error.message || 'Errore nell\'aggiornamento della segnalazione';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Modifica Segnalazione
          </h1>
          <p className="text-gray-600">
            Aggiorna la posizione, i dettagli della connessione e la descrizione del problema
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Map Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">1. Posizione sulla Mappa</h2>
            <p className="text-gray-600 mb-4">
              Puoi spostare il marker cliccando sulla mappa o cercando un nuovo indirizzo.
            </p>

            <GoogleMap
              center={selectedPosition || { latitude: 41.9028, longitude: 12.4964 }}
              zoom={15}
              height="400px"
              showSearchBox={true}
              onMapClick={handleMapClick}
              onAddressSelect={handleAddressSelect}
              markers={selectedPosition ? [{
                position: selectedPosition,
                title: 'Posizione segnalazione',
                info: 'Problema segnalato qui'
              }] : []}
            />

            {selectedPosition && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-blue-800 text-sm">
                  📍 Posizione: {selectedPosition.latitude.toFixed(6)}, {selectedPosition.longitude.toFixed(6)}
                </p>
              </div>
            )}
          </div>

          {/* Location Details */}
          <LocationDetailsForm
            formData={{
              streetName: formData.streetName || '',
              streetNameCorrection: formData.streetNameCorrection || ''
            }}
            onChange={(data) => setFormData(prev => ({ ...prev, ...data }))}
            showTitle={true}
            title="2. Dettagli della Posizione"
          />

          {/* Current Connection */}
          <ConnectionInfoForm
            currentLine={formData.currentLine || {}}
            onChange={(data) => setFormData(prev => ({
              ...prev,
              currentLine: { ...prev.currentLine, ...data }
            }))}
            showTitle={true}
            title="3. Connessione Attuale"
          />

          {/* Problem Report */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">4. Descrizione del Problema</h2>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo di Problema
                </label>
                <select
                  value={reportData.issue}
                  onChange={(e) => setReportData(prev => ({ ...prev, issue: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="no_fiber">Fibra ottica non disponibile</option>
                  <option value="slow_speed">Velocità troppo bassa</option>
                  <option value="connection_issues">Problemi di connessione</option>
                  <option value="service_unavailable">Servizio non disponibile</option>
                  <option value="other">Altro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priorità
                </label>
                <select
                  value={reportData.priority}
                  onChange={(e) => setReportData(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Bassa</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrizione Dettagliata
              </label>
              <textarea
                value={reportData.description}
                onChange={(e) => setReportData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Descrivi il problema in dettaglio..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center space-x-4">
            <button
              type="button"
              onClick={() => navigate('/my-reports')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>

            <button
              type="submit"
              disabled={isLoading || !selectedPosition}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : null}
              {isLoading ? 'Salvando...' : 'Salva Modifiche'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditReportPage;